import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import { ExpandLess, ExpandMore, ChevronLeft } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled components
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: 300,
  height: "100%",
  "& .MuiDrawer-paper": {
    width: 300,
    position: "relative",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    borderRight: "1px solid #FFE5E0",
    height: "100%",
    overflow: "hidden",
    boxShadow: "2px 0 4px rgba(255, 83, 54, 0.05)",
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: 280,
    backgroundColor: "#ffffff",
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
      margin: "4px 0",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      border: "2px solid #f1f1f1",
      "&:hover": {
        background: "#666",
      },
    },
    scrollbarWidth: "thin",
    scrollbarColor: "#888 #f1f1f1",
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, level = 0, selected }) => ({
  paddingLeft: theme.spacing(2 + level * 2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  margin: theme.spacing(0.5, 1),
  borderRadius: "8px",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: alpha("#FF5336", 0.04),
    "& .MuiListItemIcon-root": {
      color: "#FF5336",
    },
    "& .MuiListItemText-primary": {
      color: "#FF5336",
    },
  },
  "&.Mui-selected": {
    backgroundColor: alpha("#FF5336", 0.08),
    "&:hover": {
      backgroundColor: alpha("#FF5336", 0.12),
    },
    "& .MuiListItemIcon-root": {
      color: "#FF5336",
    },
    "& .MuiListItemText-primary": {
      color: "#FF5336",
      fontWeight: 600,
    },
  },
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 40,
  color: "#FF7A36",
  transition: "color 0.2s ease-in-out",
}));

const StyledListItemText = styled(ListItemText)(({ theme, level = 0 }) => ({
  "& .MuiListItemText-primary": {
    fontSize: level === 0 ? "0.95rem" : "0.875rem",
    fontWeight: level === 0 ? 600 : 400,
    color: "#1a1f36",
    transition: "color 0.2s ease-in-out",
    letterSpacing: level === 0 ? "-0.01em" : "normal",
  },
}));

const StyledCollapse = styled(Collapse)(({ theme }) => ({
  "& .MuiCollapse-wrapper": {
    paddingLeft: theme.spacing(1),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#FF5336",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: theme.spacing(2, 3, 1),
  marginTop: theme.spacing(2),
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: theme.spacing(3),
    right: theme.spacing(3),
    height: "1px",
    background: "linear-gradient(90deg, #FF5336 0%, #FFE5E0 100%)",
  },
}));

const NestedListItem = ({ item, level = 0, selectedPath, onSelect, onToggle, open }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isSelected = selectedPath === item.path;
  const isOpen = open[item.path] || false;
  const theme = useTheme();

  return (
    <>
      <ListItem disablePadding>
        <StyledListItemButton
          level={level}
          selected={isSelected}
          onClick={() => {
            if (hasChildren) {
              onToggle(item.path);
            } else {
              onSelect(item.path);
            }
          }}
        >
          {item.icon && (
            <StyledListItemIcon>
              {item.icon}
            </StyledListItemIcon>
          )}
          <StyledListItemText
            primary={item.title}
            level={level}
          />
          {hasChildren && (
            <Box sx={{ 
              color: isOpen ? "#FF5336" : "#666",
              transition: "color 0.2s ease-in-out",
            }}>
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>
          )}
        </StyledListItemButton>
      </ListItem>
      {hasChildren && (
        <StyledCollapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child) => (
              <NestedListItem
                key={child.path}
                item={child}
                level={level + 1}
                selectedPath={selectedPath}
                onSelect={onSelect}
                onToggle={onToggle}
                open={open}
              />
            ))}
          </List>
        </StyledCollapse>
      )}
    </>
  );
};

const Sidebar = ({
  documentationStructure,
  open,
  selectedPath,
  mobileOpen,
  onToggle,
  onSelect,
  onMobileClose,
}) => {
  const theme = useTheme();

  const renderSidebarContent = () => (
    <Box sx={{ 
      height: "100%", 
      display: "flex", 
      flexDirection: "column",
      overflow: "hidden",
      background: "linear-gradient(180deg, #FFF5F3 0%, #FFFFFF 100%)",
    }}>
      <Box sx={{ 
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: theme.spacing(1, 0),
        height: "calc(100vh - 64px)",
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#FFF5F3",
          borderRadius: "4px",
          margin: "4px 0",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#FF5336",
          borderRadius: "4px",
          border: "2px solid #FFF5F3",
          "&:hover": {
            background: "#FF7A36",
          },
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#FF5336 #FFF5F3",
      }}>
        <List component="nav" sx={{ width: "100%" }}>
          {documentationStructure.map((section, index) => (
            <React.Fragment key={section.title}>
              {index > 0 && <Divider sx={{ 
                my: 2,
                borderColor: "#FFE5E0",
                "&::before, &::after": {
                  borderColor: "#FFE5E0",
                },
              }} />}
              <SectionTitle>{section.title}</SectionTitle>
              {section.children.map((item) => (
                <NestedListItem
                  key={item.path}
                  item={item}
                  selectedPath={selectedPath}
                  onSelect={onSelect}
                  onToggle={onToggle}
                  open={open}
                />
              ))}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Box>
  );

  if (mobileOpen) {
    return (
      <MobileDrawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {renderSidebarContent()}
      </MobileDrawer>
    );
  }

  return (
    <StyledDrawer variant="permanent">
      {renderSidebarContent()}
    </StyledDrawer>
  );
};

export default Sidebar; 